/**
 * The presence page's diagnostic record (card `t_9ec98b5c`).
 *
 * Diagnosing the owner's phone used to take hours of blind log-reading: the
 * app side had no surface at all — no tracker state, no record of what the two
 * location reports answered, no readout of the OS regions the sync installed
 * or of the areas it built them from. This module is that surface's data half:
 * what the tracker did this session, what each report attempt answered, what
 * the last OS-region sync decided, and the account-wide areas pull the sync
 * builds from. `views/Settings/PresenceSettings.vue` renders it; the writers
 * are the tracker (`composables/useLocationTracking.ts`) and the sync
 * (`services/backgroundGeofence.ts`), and nothing here writes back to either.
 *
 * Three rules it is built to:
 *
 *  - **Words and times only.** The report ring keeps one result word per report
 *    (hub and cloud) and a timestamp — never a coordinate, an accuracy or a
 *    device id: a diagnostic must not become a second, unmanaged store of
 *    location data. `parseReportLog` rebuilds every entry field by field, so a
 *    key this module does not know cannot survive a round-trip.
 *  - **Tiny, and local.** The ring holds the last `REPORT_LOG_CAP` (10)
 *    attempts in `localStorage`, newest first; a storage that refuses reads or
 *    writes answers the in-memory value instead, the same posture as
 *    `utils/deviceFingerprint.ts`.
 *  - **Observation only.** Nothing here gates, starts or stops anything: the
 *    sync keeps its own rules, the tracker its own triggers. This module only
 *    remembers what they already decided.
 *
 * Module state rather than a store, like `services/presenceConsent.ts`: the
 * writers run outside component setup (the tracker's interval, the sync's
 * watchers), and the page only reads. `ref`s on purpose — the diagnostic block
 * is a template and must follow a report or a sync as it lands.
 */
import { ref } from 'vue';
import { regionBuildLine } from '../utils/geofenceRegions';
import type { GeofenceSyncResult } from './backgroundGeofence';
import type { CloudPresenceOutcome } from './cloudPresence';

// ── reading payloads (tolerant, like the rest of the services) ──────────────

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

function finite(value: unknown): number | null {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

// ── the report ring ─────────────────────────────────────────────────────────

/** Where the ring lives. */
export const REPORT_LOG_KEY = 'otto_presence_report_log';

/** How many report attempts the ring keeps. */
export const REPORT_LOG_CAP = 10;

/** The hub report's own result word (`POST /presence/location`). */
export type LocationReportResult = 'sent' | 'failed';

/** One report attempt: the two result words and when it happened. */
export interface LocationReportEntry {
  /** Epoch ms of the attempt. */
  at: number;
  /** The hub report's result — it either went out or it did not. */
  hub: LocationReportResult;
  /** The cloud report's result (`services/cloudPresence.ts`). */
  cloud: CloudPresenceOutcome;
}

const HUB_RESULTS: readonly string[] = ['sent', 'failed'];
const CLOUD_RESULTS: readonly string[] = ['sent', 'skipped', 'failed'];

/**
 * One ring entry, or `null` when a stored row is not one. Rebuilt field by
 * field on purpose: whatever a stale build (or a hand-edited key) wrote beside
 * these fields — coordinates included — is gone by the time it is read back.
 */
export function readReportEntry(raw: unknown): LocationReportEntry | null {
  const row = record(raw);
  if (!row) return null;
  const at = finite(row.at);
  const hub = text(row.hub);
  const cloud = text(row.cloud);
  if (at === null || at <= 0) return null;
  if (!hub || !HUB_RESULTS.includes(hub)) return null;
  if (!cloud || !CLOUD_RESULTS.includes(cloud)) return null;
  return { at, hub: hub as LocationReportResult, cloud: cloud as CloudPresenceOutcome };
}

/**
 * The stored ring as it is read: newest first, junk rows dropped, one entry
 * per valid row, at most `REPORT_LOG_CAP`. Total by contract — a key that does
 * not parse, an array of junk and a `null` all answer a list.
 */
export function parseReportLog(raw: string | null | undefined): LocationReportEntry[] {
  if (typeof raw !== 'string' || raw.trim() === '') return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const entries: LocationReportEntry[] = [];
  for (const row of parsed) {
    const entry = readReportEntry(row);
    if (entry) entries.push(entry);
    if (entries.length >= REPORT_LOG_CAP) break;
  }
  return entries;
}

/** Append one attempt and keep the newest `cap`, newest first. Pure. */
export function appendReportEntry(
  entries: readonly LocationReportEntry[],
  entry: LocationReportEntry,
  cap = REPORT_LOG_CAP,
): LocationReportEntry[] {
  return [entry, ...entries].slice(0, Math.max(1, cap));
}

/** The ring, newest first — the ref the diagnostic block renders. */
export const locationReportLog = ref<LocationReportEntry[]>([]);

let logHydrated = false;

/** `localStorage`, or `null` where there is none (Node, a locked-down webview). */
function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Load the ring from storage once per page session. */
function hydrateReportLog(): void {
  if (logHydrated) return;
  logHydrated = true;
  try {
    locationReportLog.value = parseReportLog(storage()?.getItem(REPORT_LOG_KEY) ?? null);
  } catch {
    locationReportLog.value = [];
  }
}

/** The ring, hydrating it from storage on first use. */
export function readLocationReportLog(): LocationReportEntry[] {
  hydrateReportLog();
  return locationReportLog.value;
}

/**
 * Record one report attempt — the tracker's own write, made in
 * `useLocationTracking.postLocation` after both reports answered.
 */
export function recordLocationReport(entry: LocationReportEntry): void {
  hydrateReportLog();
  locationReportLog.value = appendReportEntry(locationReportLog.value, entry);
  try {
    storage()?.setItem(REPORT_LOG_KEY, JSON.stringify(locationReportLog.value));
  } catch {
    // A storage that refuses writes: the ref still answers this session.
  }
}

// ── the tracker, as this session saw it ─────────────────────────────────────

/**
 * True once the native tracker's watch is live. Session state on purpose: it
 * answers "is the tracker running *now*", which per-instance composable state
 * cannot — the tracker lives in `App.vue`'s instance, the reader here.
 */
export const trackerRunning = ref(false);

/** The last word the tracker failed with, or `null`. */
export const trackerLastError = ref<string | null>(null);

/** Epoch ms of the last fix the tracker saw, or `null` for none this session. */
export const trackerLastFixAt = ref<number | null>(null);

/** A live watch — the tracker started (and any earlier error is spent). */
export function observeTrackerStart(): void {
  trackerRunning.value = true;
  trackerLastError.value = null;
}

/** The watch is gone (`stopTracking`). */
export function observeTrackerStop(): void {
  trackerRunning.value = false;
}

/**
 * The tracker refused or failed. `running` is deliberately untouched: a watch
 * callback that reports an error keeps its watch, and a failed start never
 * became running in the first place — only start/stop move that word.
 */
export function observeTrackerError(message: string): void {
  trackerLastError.value = message;
}

/** A fix arrived (the watch callback) — when, never where. */
export function observeTrackerFix(at: number): void {
  trackerLastFixAt.value = at;
}

// ── the OS-region sync, as its last pass ended ──────────────────────────────

/** What the last OS-region sync did. */
export interface GeofenceSyncRecord {
  /** Epoch ms of the sync. */
  at: number;
  /** The sync's outcome word: `registered`, `unchanged`, `cleared`, `skipped`, `failed`. */
  outcome: string;
  /** The short machine word behind the outcome (`same-set`, `no-consent`, …). */
  reason: string | null;
  /** Regions the OS holds after the pass, when the pass answered with a count. */
  registered: number | null;
  /** `regionBuildLine(build)` — the counts — when the pass built regions. */
  buildLine: string | null;
}

/** The last sync's result, or `null` before the first one this session. */
export const lastGeofenceSync = ref<GeofenceSyncRecord | null>(null);

/**
 * Record one sync result — the sync's own write, made by
 * `backgroundGeofence.syncBackgroundGeofences` for every path it can answer
 * (registered, unchanged, cleared, skipped, failed).
 */
export function recordGeofenceSyncResult(result: GeofenceSyncResult, at = Date.now()): void {
  lastGeofenceSync.value = {
    at,
    outcome: result.outcome,
    reason: result.reason ?? null,
    registered: result.registered ?? null,
    buildLine: result.build ? regionBuildLine(result.build) : null,
  };
}

// ── the areas the sync builds regions from ──────────────────────────────────

/** One area of the account-wide pull, narrowed to what the readout shows. */
export interface SyncAreaRow {
  /** The cloud area id — the uuid inside `area:<id>` region ids. */
  id: string;
  name: string;
  /** The hub the area belongs to, as the cloud names it; `null` when unnamed. */
  hubName: string | null;
  /** The radius the cloud holds; `null` when the row carried none. */
  radiusM: number | null;
}

/** The pull's answer: the rows, or the one word a surface needs to say so. */
export type SyncAreasRead = { ok: true; areas: SyncAreaRow[] } | { ok: false; reason: string };

/**
 * The areas of a `/presence/areas/all` payload (card `t_59300f4d`), read the
 * way the sync itself reads them: tolerant, with only an unidentifiable row
 * dropped. An array is accepted beside the envelope, as in `buildRegions`.
 */
export function syncAreaRows(payload: unknown): SyncAreaRow[] {
  const rows = Array.isArray(payload) ? payload : record(payload)?.areas;
  if (!Array.isArray(rows)) return [];
  const areas: SyncAreaRow[] = [];
  for (const raw of rows) {
    const row = record(raw);
    const id = row ? text(row.id) : null;
    if (!row || !id) continue;
    areas.push({
      id,
      name: text(row.name) ?? '',
      hubName: text(row.hubName),
      radiusM: finite(row.radiusM),
    });
  }
  return areas;
}

/** The read the diagnostic block makes: the sync's own pull, or an injected one. */
export interface SyncAreasDeps {
  fetchAllAreas: () => Promise<unknown>;
}

/**
 * Pull the account's areas — the exact read the sync builds regions from
 * (`cloudFetch(presenceAllAreasPath())`, card `t_59300f4d`), so "did my areas
 * reach the phone" is answered against the same source. Read-only: it changes
 * neither the sync nor what is registered. Never throws — a failed pull
 * answers the one word a surface can render.
 */
export async function readSyncAreas(deps?: SyncAreasDeps): Promise<SyncAreasRead> {
  try {
    const fetchAllAreas =
      deps?.fetchAllAreas ??
      (async () => {
        const [{ cloudFetch }, { presenceAllAreasPath }] = await Promise.all([
          import('./cloudApi'),
          import('./backgroundGeofence'),
        ]);
        return cloudFetch(presenceAllAreasPath());
      });
    return { ok: true, areas: syncAreaRows(await fetchAllAreas()) };
  } catch {
    return { ok: false, reason: 'pull-failed' };
  }
}

// ── test seam ───────────────────────────────────────────────────────────────

/** Test seam: forget this session's observations (module state only). */
export function resetPresenceDiagnosticsForTests(): void {
  locationReportLog.value = [];
  logHydrated = false;
  trackerRunning.value = false;
  trackerLastError.value = null;
  trackerLastFixAt.value = null;
  lastGeofenceSync.value = null;
}
